// F-16: 燃料アプリのテナント別設定（一覧表の区分 / 派生価格ルール）。
// `fuel_settings/{tenantId}` に保存し、doc が無いテナントは DEFAULT_FUEL_SETTINGS
// （従来ハードコード互換 = セルフ武生21SS の運用値）で従来どおり動作する。
// 客向け静的ページ（as-takefu21ss）からの参照は T-16 #9 の follow-up。
import { FUEL_TYPES } from './types.js';
/** 燃料 `fuel` に実際に適用される値引き幅（円）。 */
export function resolveFuelDiscount(d, fuel) {
    return d.common ? d.commonValue : d.byFuel[fuel];
}
/** 区分 id の許容形式。Firestore map キーとして使うため小文字英数 + ハイフンに限定。 */
export const FUEL_CATEGORY_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,29}$/;
/**
 * 区分 id として使用禁止の予約語。
 * top-level ミラー並行書込（F-16 レビュー #0）は F-17 で撤去済みだが、
 * 旧 shape doc の読取互換（getFuelPriceListRows）と、doc フィールド名と紛らわしい
 * id の混入防止のため予約は維持する。
 * （updatedAt 等の camelCase メタフィールドはパターン上そもそも一致しない）
 */
export const RESERVED_FUEL_CATEGORY_IDS = new Set([
    'rows',
    'tenantid',
]);
/** 一覧表の区分数上限（UI / Callable 共通）。 */
export const FUEL_CATEGORY_MAX = 10;
/** 区分ラベルの最大文字数（UI / Callable 共通）。 */
export const FUEL_CATEGORY_LABEL_MAX = 20;
/** 値引き幅の許容範囲（円）。 */
export const FUEL_DISCOUNT_MIN = 0;
export const FUEL_DISCOUNT_MAX = 99;
/**
 * 掛売派生行（振込 / 口座振替）を紐づける特別な区分 id。
 * この id の区分が存在するときだけ、一覧表に派生 2 行が表示される。
 */
export const KAKEURI_CATEGORY_ID = 'kakeuri';
export const DEFAULT_FUEL_CATEGORIES = [
    { id: 'aisin', label: 'アイシン' },
    { id: 'kawai', label: 'カワイ' },
    { id: KAKEURI_CATEGORY_ID, label: '掛売' },
];
function uniformByFuel(n) {
    return { haioku: n, regular: n, keiyu: n, touyu: n };
}
// 従来ハードコード互換のデフォルト値。燃料別だったのは LINE のみ（-6/-7/-8/-9）で、
// 他は共通モード。commonValue / byFuel の非適用側にも切替時の初期値として同値を入れておく。
export const DEFAULT_FUEL_PRICING = {
    cashless: { common: true, commonValue: 3, byFuel: uniformByFuel(3) },
    line: {
        common: false,
        commonValue: 6,
        byFuel: { haioku: 6, regular: 7, keiyu: 8, touyu: 9 },
    },
    member: { common: true, commonValue: 6, byFuel: uniformByFuel(6) },
    kakeuriBank: { common: true, commonValue: 1, byFuel: uniformByFuel(1) },
    kakeuriDirectDebit: { common: true, commonValue: 2, byFuel: uniformByFuel(2) },
};
export const DEFAULT_FUEL_SETTINGS = {
    categories: [...DEFAULT_FUEL_CATEGORIES],
    pricing: DEFAULT_FUEL_PRICING,
};
function isRecord(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function isValidDiscount(v) {
    return (typeof v === 'number' &&
        Number.isInteger(v) &&
        v >= FUEL_DISCOUNT_MIN &&
        v <= FUEL_DISCOUNT_MAX);
}
function sanitizeCategories(raw) {
    if (!Array.isArray(raw))
        return null;
    const out = [];
    const seen = new Set();
    for (const entry of raw) {
        if (!isRecord(entry))
            continue;
        const { id, label } = entry;
        if (typeof id !== 'string' || !FUEL_CATEGORY_ID_PATTERN.test(id))
            continue;
        if (RESERVED_FUEL_CATEGORY_IDS.has(id))
            continue;
        if (typeof label !== 'string')
            continue;
        const trimmed = label.trim();
        if (trimmed.length === 0 || trimmed.length > FUEL_CATEGORY_LABEL_MAX)
            continue;
        if (seen.has(id))
            continue;
        seen.add(id);
        out.push({ id, label: trimmed });
        if (out.length >= FUEL_CATEGORY_MAX)
            break;
    }
    return out;
}
function sanitizeByFuel(raw, defaults) {
    if (!isRecord(raw))
        return { ...defaults };
    const out = {};
    for (const fuel of FUEL_TYPES) {
        const v = raw[fuel];
        out[fuel] = isValidDiscount(v) ? v : defaults[fuel];
    }
    return out;
}
function cloneDiscount(d) {
    return { common: d.common, commonValue: d.commonValue, byFuel: { ...d.byFuel } };
}
function sanitizeFuelDiscount(raw, def) {
    if (!isRecord(raw))
        return cloneDiscount(def);
    return {
        common: typeof raw.common === 'boolean' ? raw.common : def.common,
        commonValue: isValidDiscount(raw.commonValue) ? raw.commonValue : def.commonValue,
        byFuel: sanitizeByFuel(raw.byFuel, def.byFuel),
    };
}
/**
 * 共通値のみだった旧 shape（F-16 初版）のスカラー値引きを FuelDiscount へ移行する。
 * 例: `pricing.cashlessDiscount: 3` → `{ common: true, commonValue: 3, byFuel: 全燃料 3 }`
 */
function normalizeScalarDiscount(pricingRaw, newKey, legacyKey, def) {
    if (isRecord(pricingRaw[newKey]))
        return sanitizeFuelDiscount(pricingRaw[newKey], def);
    const legacy = pricingRaw[legacyKey];
    if (isValidDiscount(legacy)) {
        return { common: true, commonValue: legacy, byFuel: uniformByFuel(legacy) };
    }
    return cloneDiscount(def);
}
/** 旧 shape の `lineDiscountByFuel`（燃料別のみ）→ FuelDiscount（燃料別モード）移行。 */
function normalizeLineDiscount(pricingRaw) {
    const def = DEFAULT_FUEL_PRICING.line;
    if (isRecord(pricingRaw.line))
        return sanitizeFuelDiscount(pricingRaw.line, def);
    if (isRecord(pricingRaw.lineDiscountByFuel)) {
        const byFuel = sanitizeByFuel(pricingRaw.lineDiscountByFuel, def.byFuel);
        return { common: false, commonValue: byFuel.haioku, byFuel };
    }
    return cloneDiscount(def);
}
/**
 * `fuel_settings/{tenantId}` の生 doc（または null）を安全な `FuelSettings` に正規化する。
 * 欠落 / 型不正のフィールドは項目単位でデフォルトへフォールバック（doc 全体は捨てない）。
 * F-16 初版の旧 pricing shape（cashlessDiscount 等のスカラー + lineDiscountByFuel）も
 * ここで新 shape（FuelDiscount）へ移行する。
 */
export function normalizeFuelSettings(raw) {
    if (!isRecord(raw)) {
        return {
            categories: [...DEFAULT_FUEL_CATEGORIES],
            pricing: {
                cashless: cloneDiscount(DEFAULT_FUEL_PRICING.cashless),
                line: cloneDiscount(DEFAULT_FUEL_PRICING.line),
                member: cloneDiscount(DEFAULT_FUEL_PRICING.member),
                kakeuriBank: cloneDiscount(DEFAULT_FUEL_PRICING.kakeuriBank),
                kakeuriDirectDebit: cloneDiscount(DEFAULT_FUEL_PRICING.kakeuriDirectDebit),
            },
        };
    }
    const categories = sanitizeCategories(raw.categories) ?? [...DEFAULT_FUEL_CATEGORIES];
    const pricingRaw = isRecord(raw.pricing) ? raw.pricing : {};
    const pricing = {
        cashless: normalizeScalarDiscount(pricingRaw, 'cashless', 'cashlessDiscount', DEFAULT_FUEL_PRICING.cashless),
        line: normalizeLineDiscount(pricingRaw),
        member: normalizeScalarDiscount(pricingRaw, 'member', 'memberDiscount', DEFAULT_FUEL_PRICING.member),
        kakeuriBank: normalizeScalarDiscount(pricingRaw, 'kakeuriBank', 'kakeuriBankDiscount', DEFAULT_FUEL_PRICING.kakeuriBank),
        kakeuriDirectDebit: normalizeScalarDiscount(pricingRaw, 'kakeuriDirectDebit', 'kakeuriDirectDebitDiscount', DEFAULT_FUEL_PRICING.kakeuriDirectDebit),
    };
    return { categories, pricing };
}
function sanitizeRow(raw) {
    if (!isRecord(raw))
        return null;
    const out = {};
    for (const fuel of FUEL_TYPES) {
        const v = raw[fuel];
        if (typeof v === 'number' && Number.isFinite(v))
            out[fuel] = v;
    }
    return out;
}
/**
 * `fuel_price_list/{tenantId}` の生 doc から「区分 id → 価格行」を取り出す。
 *
 * 2 つの doc shape をサポート:
 *  - 新 shape（F-16 以降の Callable が書く）: `{ rows: { [categoryId]: {haioku,...} } }`
 *  - 旧 shape（F-03）: `{ aisin: {...}, kawai: {...}, kakeuri: {...} }` の top-level 直置き
 *    （旧 Callable スキーマ上この 3 キー以外は存在し得ないため固定列挙で拾う）
 */
export function getFuelPriceListRows(raw) {
    if (!isRecord(raw))
        return {};
    const out = {};
    if (isRecord(raw.rows)) {
        for (const [id, row] of Object.entries(raw.rows)) {
            if (!FUEL_CATEGORY_ID_PATTERN.test(id))
                continue;
            if (RESERVED_FUEL_CATEGORY_IDS.has(id))
                continue;
            const sanitized = sanitizeRow(row);
            if (sanitized)
                out[id] = sanitized;
        }
        return out;
    }
    for (const { id } of DEFAULT_FUEL_CATEGORIES) {
        const sanitized = sanitizeRow(raw[id]);
        if (sanitized)
            out[id] = sanitized;
    }
    return out;
}
//# sourceMappingURL=fuelSettings.js.map